import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Activity,
  ChevronRight,
  ChevronLeft,
  Shield,
  Users,
  TrendingUp,
  Zap,
  Brain,
  Target,
  Lock,
  DollarSign,
  Layers,
  CheckCircle2,
  Menu,
  AlertTriangle,
  FileText,
  Wrench,
  Scale,
  Ban,
  Eye,
  Microscope,
  Info,
  Download,
  X,
  Thermometer,
  Code2,
  FileJson
} from "lucide-react";

// --- 1. GLOBAL META ---
const MQP_META = {
  definition: "Discipline-first health operating system (wellness only).",
  isNot: [
    "Medical service / diagnosis",
    "Coaching marketplace",
    "Influencer biohacking brand",
    "Personalization-first optimization engine"
  ]
};

// --- 2. ICON MAPPING ---
const ICON_MAP = {
  Activity, Zap, Lock, Scale, CheckCircle2, Shield, TrendingUp, AlertTriangle, 
  Target, Brain, DollarSign, Users, Ban, Eye, Microscope, Info, Thermometer
};

// --- 3. FLAG DEFINITIONS ---
const FLAG_DEFINITIONS = {
  PENDING_TELEMETRY: { label: "No Evidence", message: "Hypothesis: Waiting on Cohort 1 telemetry.", type: "warning" },
  REGULATORY_CONSTRAINT: { label: "Regulatory", message: "Strict wellness-only framing required.", type: "critical" },
  SCOPE_RISK: { label: "Scope Creep", message: "High complexity risk.", type: "warning" }
};

// --- 4. THE DATA LAYER ---
const MQP_PLAN = [
  {
    id: "cover",
    icon: "Activity",
    type: "cover",
    machine: {
      title: "MQP: The Decade Protocol",
      subtitle: "Consumer Health & Longevity Wellness",
      posture: {
        evidenceTier: "Mission",
        claimPosture: "Aspirational",
        regulatoryPosture: "Wellness Only",
        riskLevel: "Low"
      },
      flagCodes: [] 
    },
    copy: {
      storyMd: `We help high-performing men 35–55 optimize longevity, body composition, and cognitive performance through a disciplined, daily health system designed to run in 15 minutes a day.`,
      builderNotes: {
        constraints: ["Must not sound like a 'medical' device.", "15-min constraint is absolute product law."],
        assumptions: ["ICP is time-poor, not money-poor.", "Willingness to pay for 'done for you' > willingness to learn science."],
        caveats: [],
        openQuestions: []
      }
    }
  },
  {
    id: "exec-summary",
    icon: "Zap",
    type: "visual",
    visualType: "metrics-highlight",
    machine: {
      title: "Executive Summary",
      subtitle: "High-Performance Longevity System",
      posture: {
        evidenceTier: "Hypothesis",
        claimPosture: "Signals (Not Guarantees)",
        regulatoryPosture: "Wellness Only",
        riskLevel: "Moderate"
      },
      flagCodes: []
    },
    copy: {
      storyMd: `MQP is a pre-seed consumer health company targeting high-performing men (35-55).\n\nKey Mechanism: A 15-minute daily protocol (Strength, Sleep, Fasting, Sauna) supported by automated telemetry and protocol-aligned supplements.`,
      builderNotes: {
        constraints: [],
        assumptions: [],
        caveats: [
          "'10-15 years reversal' is an aspirational north star.",
          "Current epigenetic clocks show 1-5 years is the realistic immediate signal."
        ],
        openQuestions: ["Finalizing exact supplement stack COGS."]
      }
    }
  },
  {
    id: "problem",
    icon: "Lock",
    type: "visual",
    visualType: "problem-grid",
    machine: {
      title: "The Problem",
      subtitle: "Burnout & Fragmentation",
      posture: {
        evidenceTier: "Observation",
        claimPosture: "Market Analysis",
        regulatoryPosture: "N/A",
        riskLevel: "Low"
      },
      flagCodes: []
    },
    copy: {
      storyMd: `High-performing men experience accelerated health decline despite having resources. The gap is not information—it is the absence of a system.\n\nPain Point: Decision fatigue and inconsistency, leading to burnout misattributed as weakness.`,
      builderNotes: {
        constraints: [],
        assumptions: ["'Burnout as weakness' is the primary psychological hook."],
        caveats: [],
        openQuestions: []
      }
    }
  },
  {
    id: "market-gap",
    icon: "Scale",
    type: "visual",
    visualType: "market-matrix",
    machine: {
      title: "Market Gap",
      subtitle: "The Value vs. Accessibility Void",
      posture: {
        evidenceTier: "Strategy",
        claimPosture: "Differentiation",
        regulatoryPosture: "N/A",
        riskLevel: "Low"
      },
      flagCodes: []
    },
    copy: {
      storyMd: `The market is polarized:\n\n1. Cheap/Shallow (Apps)\n2. Expensive/Inaccessible (Concierge)\n\nMQP occupies the 'Unaddressed Middle': High results via systemization, priced for scale.`,
      builderNotes: {
        constraints: ["Must be expensive enough to sting (commitment) but cheap enough to scale."],
        assumptions: ["Target blended ARPU: $150-$200/mo."],
        caveats: ["Avoid the 'race to the bottom' ($10/mo apps) where churn is lethal."],
        openQuestions: []
      }
    }
  },
  {
    id: "solution",
    icon: "CheckCircle2",
    type: "visual",
    visualType: "checklist",
    machine: {
      title: "The Solution",
      subtitle: "Systemized Health Operating Model",
      posture: {
        evidenceTier: "Product",
        claimPosture: "Protocol Logic",
        regulatoryPosture: "Wellness Only",
        riskLevel: "Low"
      },
      flagCodes: []
    },
    copy: {
      storyMd: `MQP is a standardized health operating system. \n\n1. The Protocol: Morning, Midday, Evening blocks (15 mins total).\n2. The Stack: Protocol-aligned supplements remove friction.\n3. The Identity: 'Operator & Father' mindset reinforcement.`,
      builderNotes: {
        constraints: ["Telemetry must be read-only to prevent obsession."],
        assumptions: ["Checklists trigger dopamine loops."],
        caveats: ["Supplements are the 'trojan horse' for habit formation."],
        openQuestions: []
      }
    }
  },
  {
    id: "guardrails",
    icon: "Shield",
    type: "visual",
    visualType: "guardrails",
    machine: {
      title: "Product Guardrails",
      subtitle: "Defensibility & Discipline",
      posture: {
        evidenceTier: "Operations",
        claimPosture: "Internal Law",
        regulatoryPosture: "N/A",
        riskLevel: "Low"
      },
      flagCodes: []
    },
    copy: {
      storyMd: `Our defensibility is our discipline. We explicitly reject feature bloat.\n\nRule 1: No features unless adherence improves.\nRule 2: No added daily decision load.\nRule 3: Complexity is gated by retention.`,
      builderNotes: {
        constraints: ["Feature Gate: If it adds >30 seconds to the daily protocol, it is rejected."],
        assumptions: [],
        caveats: [],
        openQuestions: []
      }
    }
  },
  {
    id: "traction",
    icon: "TrendingUp",
    type: "visual",
    visualType: "traction-chart",
    machine: {
      title: "Traction & Proof",
      subtitle: "Adherence is the Metric",
      posture: {
        evidenceTier: "Projection",
        claimPosture: "Target Model",
        regulatoryPosture: "N/A",
        riskLevel: "Moderate"
      },
      flagCodes: ["PENDING_TELEMETRY"]
    },
    copy: {
      storyMd: `We define traction by disciplined adherence, not vanity downloads.\n\nPhase 0: Prove daily protocol adherence without coaching.\nPhase 1: Private Cohorts showing 30/60/90 day retention.\nPhase 2: Correlate adherence with improved biometrics (HRV, Sleep).`,
      builderNotes: {
        constraints: [],
        assumptions: ["Metric: 'Adherence' = % of checklists completed/week.", "Metric: 'Retention' = Active renewal of supplement stack."],
        caveats: ["Warning: Do not confuse 'app opens' with 'protocol execution.'"],
        openQuestions: []
      }
    }
  },
  {
    id: "risks",
    icon: "AlertTriangle",
    type: "visual",
    visualType: "risk-matrix",
    machine: {
      title: "Risks & Mitigations",
      subtitle: "Operational Discipline",
      posture: {
        evidenceTier: "Risk Mgmt",
        claimPosture: "Transparency",
        regulatoryPosture: "Compliance",
        riskLevel: "High"
      },
      flagCodes: [],
      riskData: [
        { 
          id: "reg", cat: "Regulatory", imp: "High", mit: "Wellness framing only. No diagnosis.", 
          owner: "Legal/Comp", trigger: "FDA Guidance Change", exclusion: "No Rx compounds.",
          evidence: { status: "public", ref: "FDA Policy Sec 501" }
        },
        { 
          id: "sup", cat: "Supply Chain", imp: "Med", mit: "3rd party tested. Dual sourcing.", 
          owner: "Ops Lead", trigger: "Vendor Failure", exclusion: "No unverified COAs.",
          evidence: { status: "internal", ref: "Supplier Audit v1" }
        },
        { 
          id: "adh", cat: "Adherence", imp: "High", mit: "Identity-based loops. No coaching dependency.", 
          owner: "Product", trigger: "Churn > 15%", exclusion: "No 'Gamification' gimmicks.",
          evidence: { status: "none", ref: "Pending Cohort 1" }
        },
        { 
          id: "clm", cat: "Claims", imp: "High", mit: "Evidence tiering system. Disclaimers.", 
          owner: "Compliance", trigger: "Marketing Audit", exclusion: "No 'Cure' language.",
          evidence: { status: "internal", ref: "Claim Matrix v2" }
        },
      ]
    },
    copy: {
      storyMd: `We treat risk as an operational constraint, not an afterthought. We transparently tier every intervention (Strong/Moderate/Emerging) and submit to regular compliance review.`,
      builderNotes: {
        constraints: ["No SARMs or grey-market compounds.", "No direct medical diagnosis.", "No 'forever chemicals' in packaging."],
        assumptions: [],
        caveats: ["No influencer-based science (must be peer-reviewed)."],
        openQuestions: []
      }
    }
  },
  {
    id: "ask",
    icon: "Target",
    type: "cover",
    isAsk: true,
    machine: {
      title: "The Ask",
      subtitle: "Pre-seed Round",
      posture: {
        evidenceTier: "Financial",
        claimPosture: "Use of Funds",
        regulatoryPosture: "N/A",
        riskLevel: "N/A"
      },
      flagCodes: []
    },
    copy: {
      storyMd: `Seeking $1.2M – $1.8M for 14–18 months of execution.\n\nAllocation:\n• MVP Protocol Engine\n• Private Cohorts (Adherence Proof)\n• Supply Chain Automation`,
      builderNotes: {
        constraints: ["Not funding 'growth marketing' until adherence is proven.", "Not funding large headcount."],
        assumptions: [],
        caveats: ["Capital is for validation of the system, not hype."],
        openQuestions: []
      }
    }
  },
];

// --- 5. UTILS ---
const requiresDisclaimer = (posture) => {
  const RISKY_TIERS = ["Hypothesis", "Projection"];
  const RISKY_CLAIMS = ["Aspirational", "Signals (Not Guarantees)", "Target Model"];
  return (
    posture.regulatoryPosture === "Wellness Only" ||
    RISKY_CLAIMS.includes(posture.claimPosture) ||
    RISKY_TIERS.includes(posture.evidenceTier)
  );
};

const downloadJSON = (data, filename) => {
  const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(data, null, 2));
  const node = document.createElement('a');
  node.setAttribute("href", dataStr);
  node.setAttribute("download", filename);
  document.body.appendChild(node);
  node.click();
  node.remove();
};

const handleExportMachine = () => {
  const data = MQP_PLAN.map(({ id, icon, type, visualType, machine }) => ({ id, icon, type, visualType, machine }));
  downloadJSON(data, "mqp_machine_v1.json");
};

const handleExportNarrative = () => {
  const data = MQP_PLAN.map(({ id, machine, copy }) => ({ id, title: machine.title, copy }));
  downloadJSON(data, "mqp_narrative_v1.json");
};

// --- 6. COMPONENTS ---

const PostureCallout = ({ posture }) => {
  if (!requiresDisclaimer(posture)) return null;
  return (
    <div className="mt-6 flex items-start gap-2 p-3 rounded bg-yellow-900/10 border border-yellow-700/30 text-xs text-yellow-500/80">
      <Info className="w-4 h-4 mt-0.5 shrink-0" />
      <div>
        <span className="font-bold uppercase text-[10px] tracking-wider block mb-1">Context: {posture.evidenceTier}</span>
        Statements here are {posture.claimPosture.toLowerCase()}. Outcomes are signals, not medical guarantees.
      </div>
    </div>
  );
};

const FlagsOverlay = ({ codes }) => {
  if (!codes || codes.length === 0) return null;
  return (
    <div className="absolute top-4 right-4 z-20 flex flex-col gap-2">
      {codes.map((code, i) => {
        const def = FLAG_DEFINITIONS[code];
        if (!def) return null;
        return (
          <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border shadow-lg backdrop-blur-md ${def.type === 'critical' ? 'bg-red-900/80 border-red-500/50 text-red-200' : 'bg-orange-900/80 border-orange-500/50 text-orange-200'}`}>
             <AlertTriangle className="w-3 h-3" />
             <span className="text-[10px] font-mono font-medium uppercase">{def.label}: {def.message}</span>
          </div>
        )
      })}
    </div>
  );
};

const CredibilityBar = ({ posture }) => {
  const getRiskColor = (r) => {
    if (r === "Low") return "text-emerald-500 border-emerald-500/30 bg-emerald-500/10";
    if (r === "Moderate") return "text-yellow-500 border-yellow-500/30 bg-yellow-500/10";
    if (r === "High") return "text-red-500 border-red-500/30 bg-red-500/10";
    return "text-stone-500 border-stone-500/30";
  };

  return (
    <div className="h-10 border-b border-stone-800 bg-stone-950 flex items-center px-4 md:px-6 justify-between text-[10px] font-mono uppercase tracking-widest select-none relative z-30">
      <div className="flex items-center space-x-4">
          <div className="hidden md:flex items-center space-x-1 text-stone-400 bg-stone-900/50 px-2 py-0.5 rounded border border-stone-800" title={MQP_META.definition}>
             <Shield className="w-3 h-3 text-emerald-500" />
             <span>Wellness Only</span>
          </div>
          <div className="hidden md:flex items-center space-x-1 text-stone-400 bg-stone-900/50 px-2 py-0.5 rounded border border-stone-800" title={MQP_META.isNot.join(", ")}>
             <Ban className="w-3 h-3 text-red-500" />
             <span>Not Medical / No Rx</span>
          </div>
          {/* Mobile Only Short Badge */}
          <div className="md:hidden flex items-center space-x-1 text-stone-400">
             <Shield className="w-3 h-3 text-emerald-500" />
             <span>Wellness Only</span>
          </div>
      </div>
      <div className="flex items-center space-x-4">
        <div className="hidden lg:flex items-center space-x-2 text-stone-500">
           <span>Evidence: <span className="text-stone-300">{posture.evidenceTier}</span></span>
        </div>
        <div className={`flex items-center space-x-2 px-2 py-0.5 rounded border ${getRiskColor(posture.riskLevel)}`}>
           <span>Risk: {posture.riskLevel}</span>
        </div>
      </div>
    </div>
  );
};

const RiskModal = ({ data, onClose }) => {
    if (!data) return null;
    return (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-stone-900 border border-stone-700 rounded-xl w-full max-w-lg p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-6 border-b border-stone-800 pb-4"><div><div className="text-xs font-mono text-red-400 uppercase tracking-widest">Risk Analysis</div><div className="text-xl font-bold text-white">{data.cat}</div></div><button onClick={onClose}><X className="w-5 h-5 text-stone-500 hover:text-white"/></button></div>
                <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4"><div className="p-3 bg-stone-800 rounded border border-stone-700"><div className="text-[10px] text-stone-500 uppercase">Operational Owner</div><div className="text-sm font-medium text-stone-200">{data.owner}</div></div><div className="p-3 bg-stone-800 rounded border border-stone-700"><div className="text-[10px] text-stone-500 uppercase">Trigger Event</div><div className="text-sm font-medium text-stone-200">{data.trigger}</div></div></div>
                    <div className="p-4 bg-emerald-900/10 border border-emerald-500/20 rounded"><div className="text-xs font-bold text-emerald-400 mb-1">Mitigation Strategy</div><div className="text-sm text-stone-300">{data.mit}</div></div>
                    <div className="p-4 bg-red-900/10 border border-red-500/20 rounded"><div className="text-xs font-bold text-red-400 mb-1">Hard Exclusion (The "No" List)</div><div className="text-sm text-stone-300">{data.exclusion}</div></div>
                    {data.evidence && (
                        <div className="flex items-center justify-between pt-2 border-t border-stone-800">
                             <div className="text-[10px] text-stone-500 uppercase">Evidence Status</div>
                             <div className={`text-xs font-mono px-2 py-0.5 rounded ${data.evidence.status === 'none' ? 'bg-red-500/10 text-red-400' : 'bg-blue-500/10 text-blue-400'}`}>
                                {data.evidence.status.toUpperCase()} [{data.evidence.ref}]
                             </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const DynamicVisual = ({ type, data, onOpenRisk }) => {
  const machine = data.machine; 
  if (type === "metrics-highlight") {
    return (
      <div className="h-full flex flex-col justify-center px-4 md:px-8 space-y-6 md:space-y-8">
        {[{ label: "Target Age", value: "35–55", sub: "High-Performing Men" }, { label: "Daily Protocol", value: "15 min", sub: "Morning / Mid / Night" }, { label: "Bio-Reversal", value: "10–15 yr*", sub: "Aspirational Target", disclaimer: "Evidence-supported lifestyle shifts typically show ~1–5 years epigenetic improvement (signals)." }].map((stat, i) => (
          <div key={i} className="relative pl-6 border-l-2 border-stone-800 group hover:border-blue-500 transition-colors duration-300">
            <div className="text-xs font-mono text-stone-500 uppercase tracking-widest mb-1">{stat.label}</div>
            <div className="text-3xl md:text-4xl font-bold text-white mb-1 group-hover:text-blue-400 transition-colors">{stat.value}</div>
            <div className="text-sm text-stone-600 font-medium">{stat.sub}</div>
            {stat.disclaimer && <div className="mt-2 text-[10px] text-yellow-500/80 leading-tight max-w-[200px] border-t border-yellow-900/30 pt-1">* {stat.disclaimer}</div>}
          </div>
        ))}
      </div>
    );
  }
  if (type === "problem-grid") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 h-full content-center p-4">
        {[{ title: "Concierge", desc: "$100k+ Unscalable", color: "bg-red-900/20 border-red-500/30" }, { title: "Biohacking", desc: "Complex & Hype", color: "bg-orange-900/20 border-orange-500/30" }, { title: "Fragmented", desc: "Decision Fatigue", color: "bg-yellow-900/20 border-yellow-500/30" }, { title: "Mass Market", desc: "Generic Results", color: "bg-stone-800/50 border-stone-600/30" }].map((item, idx) => (
          <div key={idx} className={`p-4 rounded-xl border ${item.color} backdrop-blur-sm flex flex-col justify-center`}><div className="text-sm font-mono opacity-70 mb-1">Fail Mode 0{idx + 1}</div><div className="font-bold text-lg text-white">{item.title}</div><div className="text-xs text-stone-400 mt-2">{item.desc}</div></div>
        ))}
      </div>
    );
  }
  if (type === "market-matrix") {
      return (
          <div className="h-96 md:h-full w-full p-6 relative flex items-center justify-center">
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><div className="w-full h-px bg-stone-700 relative"><span className="absolute right-2 -top-5 text-[10px] text-stone-500">ACCESSIBILITY →</span></div><div className="h-full w-px bg-stone-700 absolute"><span className="absolute top-2 left-2 text-[10px] text-stone-500">RESULTS ↑</span></div></div>
              <div className="absolute top-8 left-8 p-2 text-red-400 text-xs border border-red-900/30 bg-red-900/10 rounded"><div className="font-bold">Concierge</div></div>
              <div className="absolute bottom-8 right-8 p-2 text-stone-400 text-xs border border-stone-700/30 bg-stone-800/50 rounded"><div className="font-bold">Generic Apps</div></div>
              <div className="absolute top-8 right-20 p-2 text-orange-400 text-xs border border-orange-900/30 bg-orange-900/10 rounded"><div className="font-bold">Biohacking</div><div className="opacity-70 text-[8px]">Hype / Inconsistent</div></div>
              <div className="absolute bottom-20 right-20 p-2 text-yellow-500 text-xs border border-yellow-900/30 bg-yellow-900/10 rounded"><div className="font-bold">Fragmented Tools</div><div className="opacity-70 text-[8px]">Cognitive Load</div></div>
              <div className="absolute top-8 right-8 p-4 bg-blue-600 shadow-[0_0_30px_rgba(37,99,235,0.4)] text-white text-sm rounded-lg border border-blue-400 z-10"><div className="font-bold flex items-center gap-2"><Zap className="w-4 h-4 text-yellow-300 fill-yellow-300"/> MQP</div><div className="opacity-90 text-[10px] mt-1">High Result / Scalable</div></div>
          </div>
      )
  }
  if (type === "checklist") {
    return (
      <div className="bg-stone-900/50 border border-stone-700/50 rounded-xl p-6 h-full flex flex-col justify-center space-y-4">
        <div className="flex items-center space-x-2 mb-4 border-b border-stone-700 pb-2"><div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div><span className="text-xs font-mono text-emerald-500 uppercase">Live Protocol</span></div>
        {[{ time: "06:00", task: "Hydration + Light", status: "complete" }, { time: "06:15", task: "Zone 2 / Strength Block", status: "complete" }, { time: "07:00", task: "Protein + Supp Stack A", status: "complete" }, { time: "13:00", task: "Check-in // Reset", status: "pending" }, { time: "21:00", task: "Sleep Hygiene Protocol", status: "pending" }].map((item, idx) => (
          <div key={idx} className="flex items-center justify-between group"><div className="flex items-center space-x-3"><div className={`w-5 h-5 rounded border flex items-center justify-center ${item.status === 'complete' ? 'bg-emerald-500/20 border-emerald-500' : 'border-stone-600'}`}>{item.status === 'complete' && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}</div><span className={`text-sm ${item.status === 'complete' ? 'text-white' : 'text-stone-500'}`}>{item.task}</span></div><span className="text-xs font-mono text-stone-600">{item.time}</span></div>
        ))}
      </div>
    );
  }
  if (type === "guardrails") {
      return (
          <div className="h-full flex flex-col justify-center px-4 space-y-6">
              <div className="text-center mb-4"><div className="text-xs font-mono text-stone-500 uppercase">Defense Mechanism</div><div className="text-lg font-bold text-white">The Feature Gate</div></div>
              <div className="flex flex-col items-center space-y-2">
                  <div className="w-full p-3 bg-stone-800 border border-stone-700 rounded text-center text-sm text-stone-300">Proposed Feature</div>
                  <div className="h-6 w-px bg-stone-700"></div>
                  <div className="flex flex-col items-center relative">
                     <div className="w-12 h-12 rounded-full bg-stone-900 border-2 border-yellow-500 flex items-center justify-center z-10"><Scale className="w-6 h-6 text-yellow-500" /></div>
                     <div className="absolute left-14 top-2 w-32 bg-stone-800 text-[9px] text-stone-400 p-2 rounded border border-stone-700">Is Time Cost &gt; 30s?<br/><span className="text-red-400 font-bold">YES = REJECT</span></div>
                  </div>
                  <div className="text-[9px] font-mono text-yellow-500 uppercase mt-2">Adherence Audit</div>
                  <div className="h-6 w-px bg-stone-700"></div>
                  <div className="grid grid-cols-2 gap-4 w-full"><div className="p-3 bg-red-900/20 border border-red-500/30 rounded text-center opacity-50"><div className="text-xs font-bold text-red-400">Lowers Adherence</div><div className="text-[10px] text-red-300 mt-1">REJECT</div></div><div className="p-3 bg-emerald-900/20 border border-emerald-500/30 rounded text-center shadow-[0_0_15px_rgba(16,185,129,0.2)]"><div className="text-xs font-bold text-emerald-400">Improves Adherence</div><div className="text-[10px] text-emerald-300 mt-1">SHIP</div></div></div>
              </div>
          </div>
      )
  }
  if (type === "traction-chart") {
    return (
        <div className="h-full flex flex-col justify-center px-4">
            <div className="mb-6 flex justify-between items-center border-b border-stone-800 pb-2"><div className="text-xs font-mono text-stone-500 uppercase">Retention Cohorts</div><div className="flex items-center space-x-3 text-[10px]"><div className="flex items-center gap-1"><div className="w-2 h-0.5 bg-blue-500"></div><span className="text-blue-400">Target (MQP)</span></div><div className="flex items-center gap-1"><div className="w-2 h-0.5 bg-stone-600 border-t border-dashed"></div><span className="text-stone-500">Industry Avg</span></div></div></div>
            <div className="relative h-48 w-full border-l border-b border-stone-700"><svg className="absolute inset-0 h-full w-full" preserveAspectRatio="none"><path d="M0,10 Q50,150 300,180" fill="none" stroke="#44403c" strokeWidth="2" strokeDasharray="4 4" /></svg><svg className="absolute inset-0 h-full w-full" preserveAspectRatio="none"><path d="M0,10 Q100,20 300,30" fill="none" stroke="#3b82f6" strokeWidth="2" strokeDasharray="6 4" /></svg><svg className="absolute inset-0 h-full w-full overflow-visible" preserveAspectRatio="none"><path d="M0,10 L40,14" fill="none" stroke="#10b981" strokeWidth="3" /><circle cx="40" cy="14" r="3" fill="#10b981" /><text x="45" y="10" className="text-[9px] fill-emerald-500 font-mono">Actual (Early)</text></svg><div className="absolute bottom-0 left-0 translate-y-4 text-[10px] text-stone-500 font-mono">Day 0</div><div className="absolute bottom-0 left-1/2 translate-y-4 text-[10px] text-stone-500 font-mono">Day 60</div><div className="absolute bottom-0 right-0 translate-y-4 text-[10px] text-stone-500 font-mono">Day 90+</div></div>
            <div className="grid grid-cols-3 gap-2 mt-8"><div className="p-2 bg-stone-900 rounded border border-stone-800 text-center relative overflow-hidden"><div className="absolute top-0 left-0 w-full text-[8px] bg-stone-800 text-stone-500">TARGET</div><div className="text-xs text-stone-500 uppercase mt-2">Checklist %</div><div className="text-lg font-bold text-emerald-400">92%</div></div><div className="p-2 bg-stone-900 rounded border border-stone-800 text-center relative overflow-hidden"><div className="absolute top-0 left-0 w-full text-[8px] bg-stone-800 text-stone-500">TARGET</div><div className="text-xs text-stone-500 uppercase mt-2">Reorder</div><div className="text-lg font-bold text-emerald-400">85%</div></div><div className="p-2 bg-stone-900 rounded border border-stone-800 text-center relative overflow-hidden"><div className="absolute top-0 left-0 w-full text-[8px] bg-stone-800 text-stone-500">ACTUAL</div><div className="text-xs text-stone-500 uppercase mt-2">Paid Hype</div><div className="text-lg font-bold text-stone-600">0%</div></div></div>
        </div>
    );
  }
  if (type === "risk-matrix" && machine.riskData) {
      return (
          <div className="h-full flex flex-col justify-center px-2 overflow-y-auto">
              <div className="text-[10px] text-center text-stone-500 mb-2 italic">Click row for full mitigation detail</div>
              <table className="w-full text-left border-collapse">
                  <thead><tr className="text-[10px] text-stone-500 uppercase border-b border-stone-800"><th className="pb-2 pl-2">Risk Category</th><th className="pb-2">Impact</th><th className="pb-2">Mitigation</th></tr></thead>
                  <tbody className="text-xs">
                      {machine.riskData.map((risk, i) => (
                          <tr key={i} onClick={() => onOpenRisk(risk)} className="border-b border-stone-800/50 hover:bg-stone-800/60 hover:text-white cursor-pointer transition-colors group">
                              <td className="py-3 pl-2 font-medium text-stone-300 group-hover:text-white">{risk.cat}</td>
                              <td className="py-3"><span className={`px-1.5 py-0.5 rounded text-[9px] uppercase ${risk.imp === 'High' ? 'bg-red-900/30 text-red-400' : risk.imp === 'Med' ? 'bg-yellow-900/30 text-yellow-400' : 'bg-stone-800 text-stone-400'}`}>{risk.imp}</span></td>
                              <td className="py-3 text-stone-500 group-hover:text-stone-300 transition-colors truncate max-w-[120px]">{risk.mit}</td>
                          </tr>
                      ))}
                  </tbody>
              </table>
          </div>
      )
  }
  return <div className="h-full flex items-center justify-center text-stone-700"><Layers className="w-12 h-12 opacity-20" /></div>;
};

// --- 7. MAIN APP ---

const App = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [isSidebarOpen, setSidebarOpen] = useState(false); // Closed by default on mobile
  const [viewMode, setViewMode] = useState('story'); 
  const [selectedRisk, setSelectedRisk] = useState(null);

  const activeSlide = MQP_PLAN[activeIdx];
  const ActiveIcon = ICON_MAP[activeSlide.icon] || Layers;
  const machine = activeSlide.machine;
  const copy = activeSlide.copy;

  // Ensure sidebar is open on desktop mount
  useEffect(() => {
    if (window.innerWidth >= 768) setSidebarOpen(true);
  }, []);

  const nextSlide = useCallback(() => setActiveIdx(prev => Math.min(prev + 1, MQP_PLAN.length - 1)), []);
  const prevSlide = useCallback(() => setActiveIdx(prev => Math.max(prev - 1, 0)), []);

  const planHealth = useMemo(() => {
    const highRisk = MQP_PLAN.filter(s => s.machine.posture.riskLevel === "High").length;
    const aspirational = MQP_PLAN.filter(s => s.machine.posture.claimPosture === "Aspirational").length;
    return { highRisk, aspirational };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") nextSlide();
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") prevSlide();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide]);

  return (
    <div className="w-full h-screen bg-stone-950 text-stone-200 overflow-hidden font-sans selection:bg-blue-500/30 flex flex-col relative">
      
      {/* 1. TOP BAR: CREDIBILITY & POSTURE */}
      <CredibilityBar posture={machine.posture} />

      {/* 2. RISK MODAL OVERLAY */}
      {selectedRisk && <RiskModal data={selectedRisk} onClose={() => setSelectedRisk(null)} />}

      <div className="flex-1 flex overflow-hidden relative">
        <div className="absolute inset-0 z-0 pointer-events-none opacity-10" style={{ backgroundImage: "linear-gradient(#333 1px, transparent 1px), linear-gradient(90deg, #333 1px, transparent 1px)", backgroundSize: "40px 40px" }}></div>

        {/* 3. SIDEBAR (RESPONSIVE OFF-CANVAS) */}
        {/* Backdrop for Mobile */}
        {isSidebarOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
        )}
        
        <div className={`
            fixed inset-y-0 left-0 z-50 flex flex-col border-r border-stone-800 bg-stone-950/95 backdrop-blur-md transition-transform duration-300
            ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
            md:translate-x-0 md:relative md:flex
            ${isSidebarOpen ? "md:w-64" : "md:w-20"}
        `}>
          <div className="h-14 flex items-center px-6 border-b border-stone-800 justify-between">
            {isSidebarOpen ? <span className="font-bold tracking-widest text-sm text-stone-100">MQP READER</span> : <span className="font-bold">MQP</span>}
            {/* Mobile Close Button */}
            <button className="md:hidden" onClick={() => setSidebarOpen(false)}><X className="w-5 h-5 text-stone-400" /></button>
          </div>
          
          <div className="flex-1 overflow-y-auto py-4 space-y-1">
            {MQP_PLAN.map((section, idx) => {
                const SIcon = ICON_MAP[section.icon] || Layers;
                return (
                  <button key={section.id} 
                    onClick={() => { setActiveIdx(idx); setSidebarOpen(false); }} 
                    className={`w-full flex items-center px-6 py-3 text-left transition-colors duration-200 border-l-2 ${activeIdx === idx ? "border-blue-500 bg-blue-900/10 text-white" : "border-transparent text-stone-500 hover:text-stone-300 hover:bg-stone-900"}`}
                  >
                    <span className={`${activeIdx === idx ? "text-blue-400" : "opacity-70"}`}><SIcon className="w-4 h-4" /></span>
                    {(isSidebarOpen || window.innerWidth < 768) && <div className="ml-3"><div className="text-xs font-medium leading-none">{section.machine.title}</div></div>}
                  </button>
                )
            })}
          </div>

          <div className="p-4 border-t border-stone-800 space-y-4">
             {isSidebarOpen && (
                 <div className="flex justify-between text-[9px] text-stone-500 font-mono"><span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-red-500"/> {planHealth.highRisk} High Risk</span><span className="flex items-center gap-1"><Target className="w-3 h-3 text-blue-500"/> {planHealth.aspirational} Asp.</span></div>
             )}
             <div className="flex bg-stone-900 rounded p-1 border border-stone-800">
                <button onClick={() => setViewMode('story')} className={`flex-1 flex items-center justify-center py-1.5 rounded text-xs transition-colors ${viewMode === 'story' ? 'bg-stone-700 text-white shadow' : 'text-stone-500 hover:text-stone-300'}`}><FileText className="w-3 h-3 mr-1" /> {isSidebarOpen && "Investor"}</button>
                <button onClick={() => setViewMode('builder')} className={`flex-1 flex items-center justify-center py-1.5 rounded text-xs transition-colors ${viewMode === 'builder' ? 'bg-blue-900/50 text-blue-200 shadow border border-blue-500/30' : 'text-stone-500 hover:text-stone-300'}`}><Wrench className="w-3 h-3 mr-1" /> {isSidebarOpen && "Builder"}</button>
             </div>
          </div>
        </div>

        {/* 4. MAIN CANVAS */}
        <div className="flex-1 flex flex-col relative z-0 overflow-hidden">
          {/* Mobile Header */}
          <div className="h-14 flex items-center justify-between px-6 border-b border-stone-800 md:hidden bg-stone-900/90 backdrop-blur shrink-0">
            <div className="font-bold">MQP</div>
            <button onClick={() => setSidebarOpen(true)}><Menu /></button>
          </div>
          
          {/* Export Actions (Responsive Position) */}
          <div className="absolute top-16 md:top-4 right-4 md:right-6 z-20 flex space-x-2">
              <button onClick={handleExportMachine} className="flex items-center space-x-1 px-3 py-1.5 bg-stone-800 border border-stone-700 rounded hover:bg-stone-700 text-stone-400 hover:text-white transition-colors text-xs" title="Export Machine Logic (JSON)"><Code2 className="w-3 h-3" /> <span className="hidden sm:inline">Logic</span></button>
              <button onClick={handleExportNarrative} className="flex items-center space-x-1 px-3 py-1.5 bg-stone-800 border border-stone-700 rounded hover:bg-stone-700 text-stone-400 hover:text-white transition-colors text-xs" title="Export Narrative (Copy)"><FileJson className="w-3 h-3" /> <span className="hidden sm:inline">Copy</span></button>
          </div>

          <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 md:p-12">
            <div className="w-full max-w-6xl mx-auto min-h-full flex flex-col md:flex-row gap-8 pb-20 animate-in fade-in slide-in-from-bottom-4 duration-500" key={activeIdx}>
              
              {/* LEFT: CONTENT */}
              <div className={`flex-1 flex flex-col justify-start md:justify-center ${activeSlide.type === "cover" ? "items-center text-center" : "items-start text-left"}`}>
                
                <div className="mb-6 flex items-center space-x-2">
                  <span className="px-2 py-1 rounded bg-stone-800 border border-stone-700 text-[10px] font-mono text-stone-400 uppercase tracking-widest">{activeSlide.id.replace("-", " ")}</span>
                  {viewMode === 'builder' && <span className="px-2 py-1 rounded bg-blue-900/30 border border-blue-500/50 text-[10px] font-mono text-blue-400 uppercase tracking-widest animate-pulse">Internal Notes</span>}
                </div>

                <div className="flex items-center gap-3 mb-2">
                   {activeSlide.type !== 'cover' && <ActiveIcon className="w-6 h-6 md:w-8 md:h-8 text-blue-500/50" />}
                   <h1 className={`font-bold text-white leading-tight ${activeSlide.type === "cover" ? "text-4xl md:text-7xl" : "text-3xl md:text-5xl"}`}>{machine.title}</h1>
                </div>
                
                <h2 className={`text-lg md:text-2xl text-stone-400 mb-8 font-light ${activeSlide.type === "cover" ? "text-blue-400" : ""}`}>{machine.subtitle}</h2>

                {viewMode === 'story' ? (
                    <div className="relative w-full">
                        <div className={`prose prose-invert prose-stone max-w-xl text-base md:text-lg leading-relaxed whitespace-pre-line ${activeSlide.type === "cover" ? "text-stone-400" : "text-stone-300"}`}>
                            {copy.storyMd}
                        </div>
                        {activeSlide.type === 'cover' && (
                            <div className="mt-8 border-t border-stone-800 pt-6 text-left max-w-lg w-full">
                                <div className="flex items-start gap-2 text-sm text-stone-300 mb-2">
                                    <Shield className="w-4 h-4 text-emerald-500 mt-0.5"/>
                                    <span className="font-mono text-xs uppercase tracking-wide">Category:</span> <span className="flex-1">{MQP_META.definition}</span>
                                </div>
                                <div className="flex items-start gap-2 text-sm text-stone-400">
                                    <Ban className="w-4 h-4 text-red-500 mt-0.5"/>
                                    <span className="font-mono text-xs uppercase tracking-wide">Exclusions:</span>
                                </div>
                                <ul className="list-disc pl-11 text-xs text-stone-500 space-y-1 mt-1">
                                    {MQP_META.isNot.map((item, i) => <li key={i}>{item}</li>)}
                                </ul>
                            </div>
                        )}
                        <PostureCallout posture={machine.posture} />
                    </div>
                ) : (
                    <div className="bg-stone-900/50 border border-stone-800 rounded-xl p-6 max-w-xl w-full space-y-4">
                        <div className="flex items-center space-x-2 text-blue-400 mb-2"><Wrench className="w-4 h-4" /><span className="text-xs font-mono uppercase tracking-widest">Builder Configuration</span></div>
                        {copy.builderNotes.constraints.length > 0 && (<div className="space-y-1"><div className="text-[10px] text-red-400 uppercase font-bold">Hard Constraints</div>{copy.builderNotes.constraints.map((n, i) => <div key={i} className="text-sm text-stone-300 border-l-2 border-red-500/50 pl-2">{n}</div>)}</div>)}
                        {copy.builderNotes.assumptions.length > 0 && (<div className="space-y-1"><div className="text-[10px] text-blue-400 uppercase font-bold">Assumptions</div>{copy.builderNotes.assumptions.map((n, i) => <div key={i} className="text-sm text-stone-300 border-l-2 border-blue-500/50 pl-2">{n}</div>)}</div>)}
                        {copy.builderNotes.caveats.length > 0 && (<div className="space-y-1"><div className="text-[10px] text-yellow-400 uppercase font-bold">Caveats & Disclaimers</div>{copy.builderNotes.caveats.map((n, i) => <div key={i} className="text-sm text-stone-300 border-l-2 border-yellow-500/50 pl-2">{n}</div>)}</div>)}
                    </div>
                )}

                {activeSlide.isAsk && viewMode === 'story' && (
                  <div className="mt-8 flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                      <button className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded font-medium transition-colors shadow-lg shadow-blue-900/50 text-sm w-full sm:w-auto">Request Data Room</button>
                      <button className="px-6 py-3 border border-stone-700 hover:bg-stone-800 text-stone-300 rounded font-medium transition-colors text-sm w-full sm:w-auto">View Milestones</button>
                  </div>
                )}
              </div>

              {/* RIGHT: DYNAMIC VISUALS (Stacked on Mobile) */}
              {activeSlide.type !== "cover" && (
                <div className="w-full md:w-1/3 md:min-w-[350px] mt-8 md:mt-0">
                  <div className="h-full w-full bg-stone-900/30 border border-stone-800 rounded-2xl p-2 relative overflow-hidden backdrop-blur-sm min-h-[400px]">
                    <FlagsOverlay codes={machine.flagCodes} />
                    
                    <div className="absolute top-4 left-4 right-4 flex justify-between items-center pb-2 border-b border-stone-800/50 z-10">
                      <div className="flex space-x-1"><div className="w-2 h-2 rounded-full bg-stone-700"></div><div className="w-2 h-2 rounded-full bg-stone-700"></div></div>
                      <div className="text-[9px] font-mono text-stone-600">FIG. 0{activeIdx}</div>
                    </div>
                    <div className="h-full pt-10">
                      <DynamicVisual type={activeSlide.visualType} data={activeSlide} onOpenRisk={setSelectedRisk} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="h-16 border-t border-stone-800 flex items-center justify-between px-6 md:px-12 bg-stone-950 z-10 shrink-0">
            <div className="text-xs text-stone-500 font-mono hidden md:block">SECTION {activeIdx + 1} / {MQP_PLAN.length}</div>
            <div className="flex items-center space-x-4 w-full md:w-auto justify-between md:justify-end">
              <button onClick={prevSlide} disabled={activeIdx === 0} className="p-2 rounded-full border border-stone-800 hover:bg-stone-800 disabled:opacity-30 transition-colors"><ChevronLeft className="w-5 h-5" /></button>
              <span className="md:hidden text-xs text-stone-500 font-mono">{activeIdx + 1} / {MQP_PLAN.length}</span>
              <button onClick={nextSlide} disabled={activeIdx === MQP_PLAN.length - 1} className="flex items-center space-x-2 px-4 py-2 rounded-full bg-stone-100 text-stone-900 hover:bg-white disabled:opacity-50 transition-colors"><span className="text-sm font-bold">Next</span><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;
